import { useState } from 'react';
import { t } from '@/lib/i18n/messages';
import { setJuthoorPricing, approveJuthoorInitiative, rejectJuthoorInitiative } from '@/lib/api/juthoor-admin';
import type { Locale } from '@/types/auth';
import type { JuthoorInitiative, AdminPricingFormState, PricingPreview } from '@/types/juthoor';

export interface JuthoorAdminApprovalProps {
  initiative: JuthoorInitiative;
  locale: Locale;
  onApproveSuccess: () => void;
  onRejectSuccess: () => void;
}

function derivePricing(
  form: AdminPricingFormState,
  totalTrees: number
): PricingPreview {
  const purchasePrice = parseFloat(form.netzero_purchase_price) || 0;
  const sellingPrice = parseFloat(form.client_selling_price) || 0;
  const atharShare = parseFloat(form.athar_profit_share) || 0;
  const municipalityShare = parseFloat(form.municipality_profit_share) || 0;

  const margin_per_tree = sellingPrice - purchasePrice;
  const athar_profit_per_tree = (margin_per_tree * atharShare) / 100;
  const municipality_profit_per_tree = (margin_per_tree * municipalityShare) / 100;
  const total_purchase_cost = purchasePrice * totalTrees;
  const estimated_total_revenue = sellingPrice * totalTrees;
  const estimated_total_margin = margin_per_tree * totalTrees;

  const is_valid =
    sellingPrice > purchasePrice &&
    sellingPrice > 0 &&
    purchasePrice > 0 &&
    Math.abs(atharShare + municipalityShare - 100) < 0.001;

  return {
    margin_per_tree,
    athar_profit_per_tree,
    municipality_profit_per_tree,
    total_purchase_cost,
    estimated_total_revenue,
    estimated_total_margin,
    is_valid,
  };
}

export function JuthoorAdminApproval({
  initiative,
  locale,
  onApproveSuccess,
  onRejectSuccess,
}: JuthoorAdminApprovalProps) {
  const isRTL = locale === 'ar';
  const totalTrees = initiative.juthoor.total_trees;

  const [form, setForm] = useState<AdminPricingFormState>({
    netzero_purchase_price: '',
    client_selling_price: '',
    athar_profit_share: '50',
    municipality_profit_share: '50',
  });
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const preview = derivePricing(form, totalTrees);

  const handleFieldChange = (field: keyof AdminPricingFormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleApproveConfirm = async () => {
    if (!preview.is_valid) return;
    setSubmitting(true);
    setActionError(null);
    try {
      const pricingResponse = await setJuthoorPricing(initiative.id, {
        netzero_purchase_price: parseFloat(form.netzero_purchase_price),
        client_selling_price: parseFloat(form.client_selling_price),
        athar_profit_share: parseFloat(form.athar_profit_share),
        municipality_profit_share: parseFloat(form.municipality_profit_share),
      });
      if (!pricingResponse.success) {
        setActionError(pricingResponse.message || t(locale, 'error'));
        setShowApproveDialog(false);
        return;
      }
      const approveResponse = await approveJuthoorInitiative(initiative.id);
      if (approveResponse.success) {
        setShowApproveDialog(false);
        onApproveSuccess();
      } else {
        setActionError(approveResponse.message || t(locale, 'error'));
        setShowApproveDialog(false);
      }
    } catch {
      setActionError(t(locale, 'error'));
      setShowApproveDialog(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectConfirm = async () => {
    if (!rejectionReason.trim()) return;
    setSubmitting(true);
    setActionError(null);
    try {
      const response = await rejectJuthoorInitiative(initiative.id, { reason: rejectionReason });
      if (response.success) {
        setShowRejectDialog(false);
        onRejectSuccess();
      } else {
        setActionError(response.message || t(locale, 'error'));
        setShowRejectDialog(false);
      }
    } catch {
      setActionError(t(locale, 'error'));
      setShowRejectDialog(false);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-2.5 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/30';
  const labelClass = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';

  const title =
    locale === 'ar'
      ? initiative.title_ar || initiative.title
      : initiative.title_en || initiative.title;

  return (
    <div className={`space-y-6 ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Summary */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700 space-y-3">
        <h2 className="font-semibold text-gray-900 dark:text-white">{title}</h2>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-gray-500 dark:text-gray-400">{t(locale, 'totalTrees')}</p>
            <p className="font-semibold">{totalTrees.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400">{isRTL ? 'تاريخ موافقة الأمانة' : 'Municipality Approved'}</p>
            <p className="font-semibold">
              {initiative.juthoor.municipality_approved_at
                ? new Date(initiative.juthoor.municipality_approved_at).toLocaleDateString()
                : '—'}
            </p>
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400">{isRTL ? 'الجهة المُعِينة' : 'Enabler'}</p>
            <p className="font-semibold">{initiative.enabler.name}</p>
          </div>
        </div>
      </div>

      {/* Pricing form */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4">{t(locale, 'setPricing')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>{t(locale, 'purchasePrice')} (NC) *</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.netzero_purchase_price}
              onChange={(e) => handleFieldChange('netzero_purchase_price', e.target.value)}
              className={inputClass}
              placeholder="0.00"
            />
          </div>
          <div>
            <label className={labelClass}>{t(locale, 'sellingPrice')} (NC) *</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.client_selling_price}
              onChange={(e) => handleFieldChange('client_selling_price', e.target.value)}
              className={inputClass}
              placeholder="0.00"
            />
          </div>
          <div>
            <label className={labelClass}>{t(locale, 'atharProfitShare')}</label>
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={form.athar_profit_share}
              onChange={(e) => handleFieldChange('athar_profit_share', e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>{t(locale, 'municipalityProfitShare')}</label>
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={form.municipality_profit_share}
              onChange={(e) => handleFieldChange('municipality_profit_share', e.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        {/* Real-time preview */}
        <div className="mt-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 space-y-2 text-sm">
          <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-3">
            {isRTL ? 'معاينة التسعير' : 'Pricing Preview'}
          </h4>
          <div className="grid grid-cols-2 gap-y-2">
            <span className="text-gray-500">{t(locale, 'marginPerTree')}</span>
            <span className="font-semibold text-end">
              {preview.margin_per_tree.toFixed(2)} NC
            </span>
            <span className="text-gray-500">{t(locale, 'totalPurchaseCost')}</span>
            <span className="font-semibold text-end">
              {preview.total_purchase_cost.toLocaleString()} NC
            </span>
            <span className="text-gray-500">{t(locale, 'estimatedRevenue')}</span>
            <span className="font-semibold text-end">
              {preview.estimated_total_revenue.toLocaleString()} NC
            </span>
          </div>
          {!preview.is_valid && (form.netzero_purchase_price || form.client_selling_price) && (
            <p className="text-xs text-red-500 mt-2">
              {isRTL
                ? 'تأكد أن سعر البيع أكبر من سعر الشراء وأن مجموع الحصص = 100%'
                : 'Ensure selling price > purchase price and profit shares sum to 100%'}
            </p>
          )}
        </div>
      </div>

      {actionError && (
        <p className="text-sm text-red-500">{actionError}</p>
      )}

      {/* Action buttons */}
      <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <button
          type="button"
          onClick={() => { setShowApproveDialog(true); setActionError(null); }}
          disabled={!preview.is_valid || submitting}
          className="flex-1 py-3 rounded-full bg-green-600 text-white font-semibold text-sm hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t(locale, 'approveInitiative')}
        </button>
        <button
          type="button"
          onClick={() => { setShowRejectDialog(true); setActionError(null); }}
          disabled={submitting}
          className="flex-1 py-3 rounded-full border border-red-500 text-red-500 font-semibold text-sm hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
        >
          {t(locale, 'rejectInitiative')}
        </button>
      </div>

      {/* Approve dialog */}
      {showApproveDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-4">
            <h3 className="font-bold text-gray-900 dark:text-white">{t(locale, 'confirmApproval')}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">{t(locale, 'approvalConfirmText')}</p>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">{t(locale, 'purchasePrice')}</span>
                <span className="font-semibold">{parseFloat(form.netzero_purchase_price).toFixed(2)} NC</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t(locale, 'sellingPrice')}</span>
                <span className="font-semibold">{parseFloat(form.client_selling_price).toFixed(2)} NC</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t(locale, 'marginPerTree')}</span>
                <span className="font-semibold">{preview.margin_per_tree.toFixed(2)} NC</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t(locale, 'estimatedRevenue')}</span>
                <span className="font-semibold">{preview.estimated_total_revenue.toLocaleString()} NC</span>
              </div>
            </div>
            <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button
                type="button"
                onClick={() => setShowApproveDialog(false)}
                disabled={submitting}
                className="flex-1 py-2.5 rounded-full border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold"
              >
                {t(locale, 'back')}
              </button>
              <button
                type="button"
                onClick={handleApproveConfirm}
                disabled={submitting}
                className="flex-1 py-2.5 rounded-full bg-green-600 text-white text-sm font-semibold disabled:opacity-50"
              >
                {submitting ? t(locale, 'loading') : t(locale, 'confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject dialog */}
      {showRejectDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-4">
            <h3 className="font-bold text-gray-900 dark:text-white">{t(locale, 'rejectInitiative')}</h3>
            <div>
              <label className={labelClass}>{t(locale, 'rejectionReason')} *</label>
              <textarea
                rows={3}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder={t(locale, 'enterRejectionReason')}
                className={`${inputClass} resize-none`}
              />
            </div>
            <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button
                type="button"
                onClick={() => setShowRejectDialog(false)}
                disabled={submitting}
                className="flex-1 py-2.5 rounded-full border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold"
              >
                {t(locale, 'back')}
              </button>
              <button
                type="button"
                onClick={handleRejectConfirm}
                disabled={submitting || !rejectionReason.trim()}
                className="flex-1 py-2.5 rounded-full bg-red-600 text-white text-sm font-semibold disabled:opacity-50"
              >
                {submitting ? t(locale, 'loading') : t(locale, 'confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
